import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { contactAPI, contentAPI } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Mail, Phone, MapPin, Send } from 'lucide-react';

export const Contact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    full_name: '',
    company_name: '',
    phone: '',
    email: '',
    case_type: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const [contactInfo, setContactInfo] = useState(null);

  useEffect(() => {
    const loadInfo = async () => {
      try {
        const res = await contentAPI.getLLPDetails();
        setContactInfo(res.data);
      } catch (err) {
        console.error('Failed to load contact info', err);
      }
    };
    loadInfo();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCaseTypeChange = (value) => {
    setFormData({ ...formData, case_type: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await contactAPI.submit(formData);
      toast.success('Thank you! Your message has been sent successfully. We will contact you soon.');
      setFormData({
        full_name: '',
        company_name: '',
        phone: '',
        email: '',
        case_type: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to submit form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-primary min-h-screen">
      {/* Hero Section */}
      <section data-testid="contact-hero" className="relative py-32 flex items-center justify-center bg-[#111827]">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-white mb-4">
            Contact Us
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            Get in touch for a confidential consultation on your corporate recovery needs
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section data-testid="contact-section" className="py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-8 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="font-heading text-3xl font-semibold text-white mb-6">
                Get In Touch
              </h2>
              <p className="text-gray-300 text-base leading-relaxed mb-8">
                Our team of experts is ready to assist you with professional corporate debt recovery and NPA resolution services. Reach out to us for a confidential discussion.
              </p>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-secondary/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Mail size={20} className="text-secondary" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Email</h3>
                    <p className="text-gray-400">{contactInfo?.email || 'contact@kapish.com'}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-secondary/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Phone size={20} className="text-secondary" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Phone</h3>
                    <p className="text-gray-400">{contactInfo?.phone || '+91-XXXXXXXXXX'}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-secondary/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <MapPin size={20} className="text-secondary" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Location</h3>
                    <p className="text-gray-400">{contactInfo?.registered_address?.en || 'India'}</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 glass-card p-6 rounded-sm">
                <p className="text-gray-300 text-sm leading-relaxed">
                  <strong className="text-secondary">Confidentiality Assured:</strong> All communications are treated with strict confidentiality. Your information is secure with us.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="glass-card p-8 rounded-sm">
              <form data-testid="contact-form" onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-2">Full Name *</label>
                  <Input
                    data-testid="input-full-name"
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    required
                    className="bg-transparent border-b border-gray-700 text-white placeholder-gray-500 focus:border-secondary focus:outline-none py-3 px-0 w-full rounded-none"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-2">Company Name *</label>
                  <Input
                    data-testid="input-company-name"
                    type="text"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    required
                    className="bg-transparent border-b border-gray-700 text-white placeholder-gray-500 focus:border-secondary focus:outline-none py-3 px-0 w-full rounded-none"
                    placeholder="Enter your company name"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-300 text-sm font-medium mb-2">Phone *</label>
                    <Input
                      data-testid="input-phone"
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="bg-transparent border-b border-gray-700 text-white placeholder-gray-500 focus:border-secondary focus:outline-none py-3 px-0 w-full rounded-none"
                      placeholder="+91-XXXXXXXXXX"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-300 text-sm font-medium mb-2">Email *</label>
                    <Input
                      data-testid="input-email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="bg-transparent border-b border-gray-700 text-white placeholder-gray-500 focus:border-secondary focus:outline-none py-3 px-0 w-full rounded-none"
                      placeholder="you@company.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-2">Case Type *</label>
                  <Select onValueChange={handleCaseTypeChange} value={formData.case_type} required>
                    <SelectTrigger data-testid="select-case-type" className="bg-transparent border-b border-gray-700 text-white focus:border-secondary rounded-none">
                      <SelectValue placeholder="Select case type" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111827] border-white/10 text-white">
                      <SelectItem value="corporate_debt">Corporate Debt Recovery</SelectItem>
                      <SelectItem value="npa_resolution">NPA Resolution</SelectItem>
                      <SelectItem value="financial_restructuring">Financial Restructuring</SelectItem>
                      <SelectItem value="legal_coordination">Legal Coordination</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-2">Message *</label>
                  <Textarea
                    data-testid="input-message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="bg-transparent border border-gray-700 text-white placeholder-gray-500 focus:border-secondary focus:outline-none p-3 w-full rounded-sm"
                    placeholder="Tell us about your requirements..."
                  />
                </div>

                <Button
                  data-testid="submit-button"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-secondary text-primary hover:bg-secondary-light px-8 py-6 text-base font-semibold uppercase tracking-wider rounded-sm btn-glow"
                >
                  {loading ? t('common.loading') : t('common.submit')}
                  {!loading && <Send className="ml-2" size={18} />}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
